import React, { useEffect, useState } from "react";
import { Plus, Pencil, Power } from "lucide-react";
import { useNavigate } from "react-router-dom";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import PageHeader from "src/components/common/PageHeader";
import Button from "src/components/ui/Button";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import ConfirmationModal from "src/components/modals/ConfirmationModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as planActions from "src/redux/slices/planSlice";
import { formatDateTime } from "src/utils/date-time";
import { Plan, PlanFeatures } from "src/types/plan.types";
import { toast } from "react-toastify";
import { currencyFormatter } from "src/utils/common";

const PlanListPage: React.FC = () => {
  const [limit, setLimit] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Plan | null>(null);

  const { plansList, editPlan } = useAppSelector((state) => state.plan);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    getPlanList(1);
  }, []);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (editPlan.data) {
      toast.success("Plan modified successfully");
      dispatch(planActions.resetEditPlan());
      handleModalClose();
      getPlanList(1);
    } else if (editPlan.error) {
      toast.error(`Error: ${editPlan.error}`);
    }
  }, [editPlan]);

  const getPlanList = (page: number) =>
    dispatch(planActions.getPlanListRequest({ page, limit }));

  const handlePageChange = (page: number) => {
    getPlanList(page);
  };

  const handlePlanStatus = (planId: string, status: "active" | "suspended") => {
    const payload = {
      _id: planId,
      status: status,
    };
    dispatch(planActions.editPlanRequest(payload));
  };

  const onConfirm = () => {
    if (selectedRow?._id) {
      handlePlanStatus(
        selectedRow?._id,
        selectedRow?.status === "active" ? "suspended" : "active"
      );
    }
  };

  const handleModalClose = () => {
    setSelectedRow(null);
    setShowModal(false);
  };

  const formatFeatures = (features?: PlanFeatures) => {
    if (!features) return "-";
    const labels: Record<keyof PlanFeatures, string> = {
      totalUsers: "Users",
      totalProducts: "Products",
      totalTransactions: "Transactions",
      totalSaleOrders: "Sale Orders",
      totalPurchaseOrders: "Purchase Orders",
      totalSuppliers: "Suppliers",
    };
    return Object.keys(labels)
      .map((key) => {
        const typedKey = key as keyof PlanFeatures;
        const value = features[typedKey];
        return `${labels[typedKey]}: ${value ?? "-"}`;
      })
      .join(", ");
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: "Plans | Invyntra",
        description:
          "Track and manage all product plans with detailed customer and pricing information.",
        keywords: "plans, products, invoices, inventory, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Header */}
        <PageHeader
          title="Plans"
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Plans", path: "/admin/plans/list" },
          ]}
        />

        <Card>
          <CardHeader
            title="Plans"
            description="List of Plans"
            actions={
              <Button onClick={() => navigate("/admin/plans/create")}>
                <Plus size={16} className="mr-1" /> Add Plan
              </Button>
            }
          />

          {/* Table */}
          <TableV2
            columns={[
              {
                key: "planName",
                header: "Plan Name",
                accessor: ({ key, row }) => (
                  <div className="w-20">{row[key]}</div>
                ),
              },
              { key: "planCode", header: "Plan Code" },
              { key: "description", header: "Description" },
              {
                key: "price",
                header: "Price",
                align: "right",
                accessor: ({ key, row }) => (
                  <div>{currencyFormatter(row[key])}</div>
                ),
              },
              { key: "durationInDays", header: "Duration In Days" },
              {
                key: "features",
                header: "Features",
                accessor: ({ key, row }) => (
                  <div className="text-xs text-gray-700">
                    {formatFeatures(row[key])}
                  </div>
                ),
              },
              {
                key: "createdAt",
                header: "Published On",
                accessor: ({ key, row }) => (
                  <div>{formatDateTime(row[key], "DD/MM/YYYY")}</div>
                ),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ key, row }) => (
                  <Badge
                    text={row?.[key] || ""}
                    color={row?.[key] === "active" ? "green" : "red"}
                  />
                ),
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ key, row }) => (
                  <div className="pr-2">
                    <button
                      className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                      onClick={() => navigate(`/admin/plans/${row._id}/edit`)}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      className={`p-2 rounded-md ${
                        row.status == "active"
                          ? "hover:bg-red-100 text-red-600"
                          : "hover:bg-green-100 text-green-600"
                      }`}
                      onClick={() => {
                        setSelectedRow(row);
                        setShowModal(true);
                      }}
                    >
                      <Power size={14} />
                    </button>
                  </div>
                ),
              },
            ]}
            data={plansList?.data?.data}
            pagination={plansList?.data?.pagination}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>

      {showModal && selectedRow?._id && (
        <ConfirmationModal
          message={`Are you sure to ${
            selectedRow?.status === "active" ? "suspend" : "activate"
          } this plan?`}
          onConfirm={onConfirm}
          onCancel={handleModalClose}
          open={showModal}
          isLoading={editPlan?.loading}
        />
      )}
    </Layout>
  );
};

export default PlanListPage;
